import { useEffect, useMemo, useRef, useState } from 'react';
import { isMissing, useAutonomyProfiles, useMapRecord, useMaps } from '../api/queries';
import { sendAutonomyUtterance, setAutonomyWake, startAutonomy, stopAutonomy, useAutonomy } from '../lib/autonomy';
import type { Lang, WakeMode } from '../api/types';
import { useT } from '../i18n';
import { useLive } from '../live/store';
import { timeStr } from '../lib/format';
import { usePopoverClose, useStoredState } from '../lib/hooks';
import { stuckPhaseLabel } from '../lib/stuck';
import { findCuts, visibleEntries, type Cut, type LogEntry } from '../lib/transcript';
import { IconMic } from './Icon';
import { Link } from 'react-router';
import { useSessionBackfill } from '../lib/autonomyHistory';
import { AutonomyLiveControls } from './AutonomyControls';
import { Select } from './Select';

export function AutonomyStateChip({ state, big }: { state: string | null; big?: boolean }) {
  const t = useT();
  if (!state) return null;
  const known = ['listening', 'thinking', 'speaking', 'moving', 'paused', 'idle'].includes(state);
  return (
    <span className={`auto-chip s-${known ? state : 'idle'}${big ? ' big' : ''}`} role="status" aria-live="polite">
      {(state === 'thinking' || state === 'moving') && <span className="spinner sm" aria-hidden />}
      {state === 'listening' && <span className="auto-dot" aria-hidden />}
      {known ? t(`auto.state.${state}` as 'auto.state.idle') : state}
    </span>
  );
}


/**
 * Transcript entries (shared by the live panel and the history page): visitor / robot bubbles, typed-by tags,
 * notes, tool calls, interrupted marks and system lines. `cuts` comes from `findCuts(entries)`.
 */
export function TranscriptEntries({ entries, robotName, cuts, pointName, idleS }: { entries: LogEntry[]; robotName?: string; cuts: Map<number, Cut>; pointName?: (pointId: string) => string | undefined; idleS?: number | null }) {
  const t = useT();
  return (
    <>
      {entries.map((e) => {
            const time = timeStr(e.ts ?? e.t);
            const gloss = typeof e.gloss === 'string' ? e.gloss : typeof e.translation === 'string' ? e.translation : null;
            if (e.kind === 'note' || (e.kind === 'reply' && e.spoken === false))
              return (
                <div key={e.id} data-mid={e.id} className="transcript-note" title={t('auto.note')}>
                  <span className="mono">{time}</span> {e.text}
                </div>
              );
            if (e.kind === 'utterance')
              return (
                <div key={e.id} data-mid={e.id} className={`bubble-wrap visitor${e.engine === 'web' ? ' typed' : ''}`}>
                  <span className="bubble-meta">
                    <span className="mono">{time}</span> ·{' '}
                    {e.engine === 'web' ? (
                      <span className="typed-tag">
                        {t('auto.typedBy', { by: typeof e.by === 'string' && e.by ? e.by : t('auto.operator') })}
                        {e.lang ? ` · ${e.lang}` : ''}
                      </span>
                    ) : (
                      <>
                        {t('auto.visitor')}
                        {e.lang ? ` · ${e.lang}` : ''}
                      </>
                    )}
                  </span>
                  <span className="bubble">
                    {e.text}
                    {gloss && <span className="gloss">{gloss}</span>}
                  </span>
                </div>
              );
            if (e.kind === 'reply') {
              const cut = cuts.get(e.id);
              const text = String(e.text ?? '');
              return (
                <div key={e.id} data-mid={e.id} className={`bubble-wrap robot${cut ? ' cut' : ''}`}>
                  <span className="bubble-meta">
                    {cut && <span className="cut-tag">⏹ {t('auto.interrupted')}</span>}
                    {robotName ?? t('auto.robot')} · <span className="mono">{time}</span>
                  </span>
                  <span className="bubble">
                    {cut && cut.spoken !== null ? (
                      <>
                        {cut.spoken}
                        <s className="unspoken" title={t('auto.unspoken')}>
                          {text.slice(cut.spoken.length)}
                        </s>
                      </>
                    ) : (
                      text
                    )}
                    {gloss && <span className="gloss">{gloss}</span>}
                  </span>
                </div>
              );
            }
            if (e.kind === 'returnHome') {
              const pid = typeof e.pointId === 'string' ? e.pointId : null;
              const point = pid ? (pointName?.(pid) ?? pid) : null;
              const reason = typeof e.reason === 'string' ? e.reason : '';
              const secs = [e.idleS, e.seconds, e.afterS, idleS].find((v) => typeof v === 'number') as number | undefined;
              const why = /idle|inactiv|timeout/i.test(reason) ? (secs !== undefined ? t('auto.home.idle', { s: Math.round(secs) }) : t('auto.home.idleNoS')) : reason;
              return (
                <div key={e.id} data-mid={e.id} className="transcript-sep home-line">
                  <span className="mono">{time}</span> ↩ {point ? t('auto.home.toPoint', { point }) : t('auto.home.back')}
                  {why ? ` — ${why}` : ''}
                </div>
              );
            }
            if (e.kind === 'navStuck') {
              const ph = String(e.phase ?? '');
              const a = typeof e.attempt === 'number' ? e.attempt : 1;
              const m = typeof e.maxAttempts === 'number' ? e.maxAttempts : 2;
              const text = ph === 'recovered' ? t('stuck.lineRecovered') : ph === 'failed' ? t('stuck.lineFailed', { a }) : t('stuck.line', { phase: stuckPhaseLabel(ph, typeof e.side === 'string' ? e.side : undefined), a, m });
              return (
                <div key={e.id} data-mid={e.id} className={`transcript-sep stuck-line ${ph === 'recovered' ? 'ok' : ph === 'failed' ? 'bad' : ''}`}>
                  <span className="mono">{time}</span> {text}
                </div>
              );
            }
            if (e.kind === 'interrupted')
              return (
                <div key={e.id} data-mid={e.id} className="transcript-sep">
                  ⏹ {t('auto.interruptedTurn')}
                </div>
              );
            if (e.kind === 'toolCall') {
              const blocked = e.ok === false || e.blocked === true || e.isError === true;
              const arg = e.input && typeof e.input === 'object' ? Object.values(e.input as Record<string, unknown>).filter((v) => typeof v === 'string' || typeof v === 'number').slice(0, 2).join(', ') : '';
              const result = typeof e.result === 'string' ? e.result : typeof e.text === 'string' ? e.text : '';
              return (
                <div key={e.id} data-mid={e.id} className={`tool-line${blocked ? ' blocked' : ''}`} title={e.input ? JSON.stringify(e.input) : undefined}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18l3 3 6.3-6.3a4 4 0 0 0 5.4-5.4l-2.5 2.5-2.4-.6-.6-2.4z" />
                  </svg>
                  <span className="grow ellipsis">
                    {e.name ?? '?'}
                    {arg ? `: ${arg}` : ''}
                  </span>
                  {(result || blocked) && <span className="muted ellipsis" style={{ maxWidth: '45%' }}>{result || t('auto.blocked')}</span>}
                </div>
              );
            }
            if (e.kind === 'ended')
              return (
                <div key={e.id} data-mid={e.id} className="transcript-sep">
                  {t('auto.ended')}
                  {e.reason ? ` · ${e.reason}` : ''}
                </div>
              );
            return (
              <div key={e.id} data-mid={e.id} className="transcript-sep">
                {e.kind} {e.text ?? ''}
              </div>
            );
          })}
    </>
  );
}


/** Live transcript (RobotAutonomy.dc.html): visitor bubbles left, robot replies right, tool calls as mono rows. */
export function AutonomyTranscript({ serial, robotName, fill, composer }: { serial: string; robotName?: string; fill?: boolean; composer?: boolean }) {
  const t = useT();
  const log = useLive((s) => s.autonomyLog[serial]);
  const clear = useLive((s) => s.clearAutonomy);
  const { active, profileId } = useAutonomy(serial);
  const [showTools, setShowTools] = useStoredState('cruzr.transcriptTools', true);
  // point names for "returnHome" lines (current map) and the profile's idle timeout as the fallback seconds
  const mapName = useLive((s) => s.robots[serial]?.status?.currentMap);
  const mapsQ = useMaps();
  const mapRec = useMapRecord(mapName ? mapsQ.data?.find((m) => m.name === mapName)?.id : undefined);
  const profilesQ = useAutonomyProfiles();
  const idleS = profilesQ.data?.find((p) => p.id === profileId)?.idle.returnHomeAfterS ?? null;
  const ref = useRef<HTMLDivElement>(null);
  // Stick to the bottom unless the operator scrolled up to read older lines.
  const stick = useRef(true);
  const [away, setAway] = useState(false);
  const toBottom = () => {
    const el = ref.current;
    if (el) el.scrollTop = el.scrollHeight;
    stick.current = true;
    setAway(false);
  };
  const onScroll = () => {
    const el = ref.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    stick.current = atBottom;
    setAway(!atBottom);
  };
  useEffect(() => {
    const el = ref.current;
    if (el && stick.current) el.scrollTop = el.scrollHeight;
  }, [log?.length, showTools]);
  // Keep the bottom pinned when the panel itself resizes (composer grows, window resizes).
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      if (stick.current) el.scrollTop = el.scrollHeight;
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const { cuts, shown } = useMemo(() => visibleEntries(log ?? [], showTools), [log, showTools]);
  // earlier messages of the running session (before this page was opened) come from the history API
  useSessionBackfill(serial);
  return (
    <section className={`transcript${fill ? ' fill' : ''}`} aria-label={t('auto.transcript')}>
      <div className="transcript-head">
        <h2 className="h2">{t('auto.transcript')}</h2>
        <div className="grow" />
        <label className="row" style={{ gap: 6, fontSize: 12, color: 'var(--muted)' }}>
          <input type="checkbox" checked={showTools} onChange={(e) => setShowTools(e.target.checked)} />
          {t('auto.showTools')}
        </label>
        <Link to={`/autonomy/history?serial=${encodeURIComponent(serial)}`} className="btn xs ghost" title={t('hist.linkTitle')}>
          {t('hist.link')}
        </Link>
        {Boolean(log?.length) && (
          <button type="button" className="btn xs ghost muted" onClick={() => clear(serial)}>
            {t('common.clear')}
          </button>
        )}
      </div>
      <div ref={ref} className="transcript-body" aria-live="polite" onScroll={onScroll}>
        {shown.length ? (
          <TranscriptEntries entries={shown} robotName={robotName} cuts={cuts} pointName={(pid) => mapRec.data?.points.find((p) => p.id === pid)?.name} idleS={idleS} />
        ) : (
          <div className="muted" style={{ fontSize: 13 }}>
            {t('auto.noTranscript')}
          </div>
        )}
      </div>
      {away && (
        <button type="button" className="btn xs jump-latest" onClick={toBottom}>
          ↓ {t('auto.jumpLatest')}
        </button>
      )}
      {composer && active && <AutonomyComposer serial={serial} onSent={toBottom} />}
    </section>
  );
}

const LANGS: Lang[] = ['uz', 'ru', 'en'];
const isLang = (v: unknown): v is Lang => v === 'uz' || v === 'ru' || v === 'en';

/** Session language: the latest language the session reported (state / utterance), else the profile default. */
function useSessionLang(serial: string): Lang {
  const log = useLive((s) => s.autonomyLog[serial]);
  const { profileId } = useAutonomy(serial);
  const profiles = useAutonomyProfiles();
  if (log)
    for (let i = log.length - 1; i >= 0; i--) {
      const e = log[i];
      if (!e) continue;
      if ((e.kind === 'state' || e.kind === 'utterance' || e.kind === 'reply') && isLang(e.lang)) return e.lang;
    }
  const p = profiles.data?.find((x) => x.id === profileId);
  return p && isLang(p.defaultLang) ? p.defaultLang : 'ru';
}

const MAX_LINES = 4;

/** Operator input: type as the visitor (replaces the voice). The bubble comes back through /ws/ui, never added locally. */
function AutonomyComposer({ serial, onSent }: { serial: string; onSent: () => void }) {
  const t = useT();
  const sessionLang = useSessionLang(serial);
  const { state } = useAutonomy(serial);
  const [pick, setPick] = useState<Lang | null>(null);
  const lang = pick ?? sessionLang;
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const ta = useRef<HTMLTextAreaElement>(null);
  const fit = (el: HTMLTextAreaElement | null) => {
    if (!el) return;
    const cs = getComputedStyle(el);
    const lh = parseFloat(cs.lineHeight) || 20;
    const chrome = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom) + parseFloat(cs.borderTopWidth) + parseFloat(cs.borderBottomWidth);
    el.style.height = 'auto';
    const full = el.scrollHeight + parseFloat(cs.borderTopWidth) + parseFloat(cs.borderBottomWidth);
    const max = lh * MAX_LINES + chrome;
    el.style.height = `${Math.min(full, max)}px`;
    el.style.overflowY = full > max ? 'auto' : 'hidden';
  };
  const send = async () => {
    const msg = text.trim();
    if (!msg || busy) return;
    setBusy(true);
    const ok = await sendAutonomyUtterance(serial, msg, lang);
    setBusy(false);
    if (ok) {
      setText('');
      requestAnimationFrame(() => {
        fit(ta.current);
        ta.current?.focus();
      });
      onSent();
    } else requestAnimationFrame(() => ta.current?.focus());
  };
  return (
    <form
      className="transcript-compose"
      onSubmit={(e) => {
        e.preventDefault();
        void send();
      }}
    >
      <textarea
        ref={ta}
        className="input"
        rows={1}
        value={text}
        disabled={busy}
        placeholder={t('auto.typePlaceholder')}
        aria-label={t('auto.typePlaceholder')}
        onChange={(e) => {
          setText(e.target.value);
          fit(e.target);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
            e.preventDefault();
            void send();
          }
        }}
      />
      <Select<Lang> size="sm" style={{ width: 64 }} ariaLabel={t('auto.typeLang')} value={lang} onChange={setPick} options={LANGS.map((l) => ({ value: l, label: l }))} disabled={busy} />
      <button type="submit" className="btn sm primary" disabled={busy || !text.trim()}>
        {busy ? <span className="spinner sm" aria-hidden /> : t('auto.typeSend')}
      </button>
      {state === 'speaking' && (
        <span className="compose-hint" role="status">
          {t('auto.bargeHint')}
        </span>
      )}
    </form>
  );
}

/** Stored microphone-mode choice used when starting autonomy ('' = profile default). */
function useWakePref() {
  return useStoredState<'' | WakeMode>('cruzr.autoWake', '');
}

/**
 * Microphone mode: "Always listening" / "Hold to talk" / "Profile default".
 * Not running → the choice is sent with the start request; running → PATCHed immediately (shows status.autonomy.wake).
 */
export function MicModeSelect({
  serial,
  profileId,
  pref,
  setPref,
  size = 'sm',
  width = 170,
}: {
  serial: string;
  profileId: string;
  pref: '' | WakeMode;
  setPref: (v: '' | WakeMode) => void;
  size?: 'sm' | 'md';
  width?: number;
}) {
  const t = useT();
  const { active, wake } = useAutonomy(serial);
  const profiles = useAutonomyProfiles();
  const [busy, setBusy] = useState(false);
  const profileWake: WakeMode = profiles.data?.find((p) => p.id === profileId)?.wake ?? 'presence';
  const value: '' | WakeMode = active ? (wake === 'always' || wake === 'hold' ? wake : '') : pref;
  const change = async (v: string) => {
    const next = v as '' | WakeMode;
    if (!active) return setPref(next);
    setBusy(true);
    await setAutonomyWake(serial, next || profileWake);
    setBusy(false);
  };
  return (
    <Select<'' | WakeMode>
      size={size}
      style={{ width }}
      ariaLabel={t('auto.micMode')}
      value={value}
      disabled={busy}
      onChange={(v) => void change(v)}
      options={[
        { value: 'always', label: t('auto.wake.always') },
        { value: 'hold', label: t('auto.wake.hold') },
        { value: '', label: t('auto.wake.default'), meta: t(`auto.wake.${profileWake}`) },
      ]}
    />
  );
}

/** Header control (RobotAutonomy.dc.html): "Autonomy" switch + profile picker. */
export function AutonomyHeaderControl({ serial, online }: { serial: string; online: boolean }) {
  const t = useT();
  const profiles = useAutonomyProfiles();
  const { active, profileId } = useAutonomy(serial);
  const [pick, setPick] = useStoredState<string>('cruzr.autoProfile', '');
  const [busy, setBusy] = useState(false);
  const [wakePref, setWakePref] = useWakePref();
  const list = profiles.data ?? [];
  const chosen = active && profileId ? profileId : list.some((p) => p.id === pick) ? pick : (list[0]?.id ?? '');
  const missing = isMissing(profiles.error);
  const flip = async () => {
    setBusy(true);
    if (active) await stopAutonomy(serial);
    else if (chosen) {
      useLive.getState().clearAutonomy(serial);
      await startAutonomy(serial, chosen, wakePref);
    }
    setBusy(false);
  };
  return (
    <div className="auto-control" title={missing ? t('auto.apiMissing') : t('auto.overrideNote')}>
      <span id={`auto-label-${serial}`} style={{ fontSize: 14, fontWeight: 600 }}>
        {t('auto.title')}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={active}
        aria-labelledby={`auto-label-${serial}`}
        className={`toggle${active ? ' on' : ''}`}
        disabled={busy || missing || (!active && (!online || !chosen))}
        onClick={() => void flip()}
      >
        <span />
      </button>
      <span className="auto-sep" aria-hidden />
      <span className="muted hide-md" style={{ fontSize: 13 }}>
        {t('auto.profile')}
      </span>
      <Select
        size="sm"
        style={{ width: 160 }}
        ariaLabel={t('auto.profile')}
        value={chosen}
        disabled={active || missing}
        onChange={setPick}
        placeholder={missing ? t('auto.unavailable') : t('auto.noProfiles')}
        options={list.map((p) => ({ value: p.id, label: p.name, meta: p.languages.join(' ') }))}
      />
      <span className="auto-sep" aria-hidden />
      <span className="muted" title={t('auto.micMode')} style={{ display: 'inline-flex' }}>
        <IconMic size={16} />
      </span>
      <MicModeSelect serial={serial} profileId={chosen} pref={wakePref} setPref={setWakePref} width={150} />
    </div>
  );
}

/** Stats row under the video while autonomy runs. */
export function AutonomyStats({ serial }: { serial: string }) {
  const t = useT();
  const log = useLive((s) => s.autonomyLog[serial]);
  const { profileId } = useAutonomy(serial);
  const profiles = useAutonomyProfiles();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const events = log ?? [];
  const start = events[0]?.t;
  const secs = start ? Math.max(0, Math.round((now - start) / 1000)) : 0;
  const turns = events.filter((e) => e.kind === 'utterance').length;
  const tools = events.filter((e) => e.kind === 'toolCall');
  const blocked = tools.filter((e) => e.ok === false || e.blocked === true || e.isError === true).length;
  const cap = profiles.data?.find((p) => p.id === profileId)?.maxSpeed;
  const cell = (k: string, v: React.ReactNode) => (
    <div className="col" style={{ gap: 3 }}>
      <span className="section-label">{k}</span>
      <span className="mono" style={{ fontSize: 15 }}>
        {v}
      </span>
    </div>
  );
  return (
    <div className="auto-stats">
      {cell(t('auto.session'), start ? `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}` : '—')}
      {cell(t('auto.turns'), turns)}
      {cell(
        t('auto.toolCalls'),
        <>
          {tools.length}
          {blocked > 0 && <span style={{ color: 'var(--accent)' }}> · {t('auto.blockedN', { n: blocked })}</span>}
        </>,
      )}
      {cell(t('auto.speedCap'), typeof cap === 'number' ? `${cap.toFixed(2)} m/s` : '—')}
    </div>
  );
}

/**
 * "On the robot screen now": what the robot actually said last, shown over the camera.
 * An interrupted reply shows only its spoken part + "…" ⏹; if nothing of it was spoken, the previous spoken reply.
 */
function captionOf(log: LogEntry[]): { text: string; cut: boolean } | null {
  const { cuts } = findCuts(log);
  for (let i = log.length - 1; i >= 0; i--) {
    const e = log[i]!;
    if (e.kind !== 'reply' || e.spoken === false || !e.text) continue;
    const cut = cuts.get(e.id);
    if (!cut) return { text: String(e.text), cut: false };
    if (cut.full) return { text: String(e.text), cut: true };
    const said = (cut.spoken ?? cut.soFar ?? '').trim();
    if (!said) {
      if (cut.soFar === null) return { text: String(e.text), cut: true }; // no spokenSoFar at all: unknown, keep the text
      continue; // nothing of it was spoken
    }
    return { text: `${said.replace(/[\s.,;:!?…—-]+$/u, '')}…`, cut: true };
  }
  return null;
}

export function AutonomyCaption({ serial }: { serial: string }) {
  const t = useT();
  const log = useLive((s) => s.autonomyLog[serial]);
  const last = useMemo(() => (log ? captionOf(log) : null), [log]);
  if (!last) return null;
  return (
    <div className="auto-caption">
      <span className="section-label">{t('auto.onScreenNow')}</span>
      <span className="auto-caption-text">
        “{last.text}”
        {last.cut && (
          <span className="cut-tag" title={t('auto.interrupted')} style={{ marginLeft: 8 }}>
            ⏹
          </span>
        )}
      </span>
    </div>
  );
}

/** Right side while autonomy runs: transcript, big Stop, "Pause and take over manually". */
export function AutonomySide({ serial, robotName, onTakeOver }: { serial: string; robotName: string; onTakeOver: () => void }) {
  const t = useT();
  const { active } = useAutonomy(serial);
  const online = useLive((s) => Boolean(s.robots[serial]?.online));
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useStoredState<'transcript' | 'controls'>('cruzr.autoSideTab', 'transcript');
  return (
    <aside className="auto-side" aria-label={t('auto.transcript')}>
      <div role="tablist" aria-label={t('auto.liveControls')} className="seg auto-side-tabs">
        <button type="button" role="tab" aria-selected={tab === 'transcript'} aria-checked={tab === 'transcript'} onClick={() => setTab('transcript')}>
          {t('auto.tabTranscript')}
        </button>
        <button type="button" role="tab" aria-selected={tab === 'controls'} aria-checked={tab === 'controls'} onClick={() => setTab('controls')}>
          {t('auto.tabControls')}
        </button>
      </div>
      {/* the transcript stays mounted (scroll position, unsent text); only hidden while Controls is open */}
      <div className="auto-side-pane" hidden={tab !== 'transcript'}>
        <AutonomyTranscript serial={serial} robotName={robotName} fill composer />
      </div>
      {tab === 'controls' && <AutonomyLiveControls serial={serial} online={online} />}
      <button
        type="button"
        className="auto-stop"
        disabled={!active || busy}
        onClick={async () => {
          setBusy(true);
          await stopAutonomy(serial);
          setBusy(false);
        }}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
          <rect x="6" y="6" width="12" height="12" rx="2" />
        </svg>
        {active ? t('auto.stop') : t('auto.stopped')}
      </button>
      <button type="button" className="btn lg" style={{ background: 'var(--panel)' }} onClick={onTakeOver} title={t('auto.overrideNote')}>
        {t('auto.takeOver')}
      </button>
    </aside>
  );
}

/** Compact Fleet-card control: state chip + stop, or "Autonomy…" with a profile picker popover. */
export function AutonomyChip({ serial, online }: { serial: string; online: boolean }) {
  const t = useT();
  const { active, state, profileId } = useAutonomy(serial);
  const profiles = useAutonomyProfiles();
  const [open, setOpen] = useState(false);
  const [pick, setPick] = useStoredState<string>('cruzr.autoProfile', '');
  const ref = usePopoverClose(open, () => setOpen(false));
  const list = profiles.data ?? [];
  const [wakePref, setWakePref] = useWakePref();
  const chosen = list.some((p) => p.id === pick) ? pick : (list[0]?.id ?? '');
  if (!online) return null;
  if (active)
    return (
      // the state and "Stop autonomy" live in the mode chip row; here only the mic mode
      <span className="row" style={{ gap: 6 }} data-state={state ?? undefined}>
        <MicModeSelect serial={serial} profileId={profileId ?? chosen} pref={wakePref} setPref={setWakePref} width={150} />
      </span>
    );
  if (isMissing(profiles.error)) return null;
  return (
    <div className="pop-anchor">
      <button type="button" className="btn xs ghost" aria-expanded={open} onClick={() => setOpen(!open)}>
        {t('auto.startShort')}
      </button>
      {open && (
        <div ref={ref} className="popover" style={{ left: 0, top: 34, width: 260 }}>
          <span className="section-label">{t('auto.profile')}</span>
          <Select
            ariaLabel={t('auto.profile')}
            value={chosen}
            onChange={setPick}
            placeholder={t('auto.noProfiles')}
            options={list.map((p) => ({ value: p.id, label: p.name, meta: p.languages.join(' ') }))}
          />
          <span className="section-label">{t('auto.mic')}</span>
          <MicModeSelect serial={serial} profileId={chosen} pref={wakePref} setPref={setWakePref} size="md" width={236} />
          <button
            type="button"
            className="menu-item primary"
            disabled={!chosen}
            onClick={async () => {
              setOpen(false);
              useLive.getState().clearAutonomy(serial);
              await startAutonomy(serial, chosen, wakePref);
            }}
          >
            {t('auto.start')}
          </button>
        </div>
      )}
    </div>
  );
}
